import { cyclesOn } from './round-config'
import type { Clean, Customer } from './types'
import { addDaysISO, cycleLabel, todayISO } from './ui'
import { visibleTime } from './business-settings'


// A cycle is only MISSING when nobody has agreed one. "Ad-hoc", "on request",
// "one off" and "not wanted" are answers — the customer was asked and said
// they have no fixed cycle — so they must not light up the missing-details
// warning the way a blank or "TBC" does. cycleLabel already recognises the
// dropdown's markers and the ad-hoc legacy spellings; the pattern below adds
// the "on request" family, which the sheet also uses and cycleLabel leaves
// blank.
// "When they ask" is the short list's word for all of them (19 Oct 2026), typed or imported as words.
const NO_FIXED_CYCLE = /^(when[\s-]?they[\s-]?ask|ad[\s-]?hoc|on[\s-]?request|as[\s-]?and[\s-]?when|when[\s-]?required|when[\s-]?needed|on[\s-]?call|on[\s-]?demand|when[\s-]?asked|as[\s-]?(needed|required))/

// An agreed "no fixed cycle" answer (ad-hoc, on request, one off, not wanted) and no cycle in days: the
// customer asks when they want one, so they need no date from the owner (the diary's "No date yet").
export function noFixedCycle(c: Pick<Customer, 'cycle_days' | 'cycle_text'>): boolean {
  if (c.cycle_days && c.cycle_days > 0) return false
  const t = (c.cycle_text ?? '').trim().toLowerCase()
  return !!t && (NO_FIXED_CYCLE.test(t) || /^(one[\s-]?off|just[\s-]?once|doesn.?t want|not wanted)/.test(t))
}

export function cycleAgreed(c: Pick<Customer, 'cycle_days' | 'cycle_text'>): boolean {
  if (c.cycle_days && c.cycle_days > 0) return true
  const t = (c.cycle_text ?? '').trim().toLowerCase()
  if (!t) return false
  if (NO_FIXED_CYCLE.test(t) || /^just[\s-]?once/.test(t)) return true
  const label = cycleLabel(c.cycle_days, c.cycle_text)
  return !!label && label !== 'to be confirmed'
}

// The cycle a due date is worked out from. Agreed cycle first; otherwise, when
// nothing was agreed and the wording is not an agreed "ad-hoc / on request",
// the guess from their past visits (customers.cycle_estimate_days) — so a
// customer whose cycle was never typed in still gets a due date, marked as
// guessed wherever it shows. An agreed no-cycle answer stays without one.
export function effectiveCycle(c: Pick<Customer, 'cycle_days' | 'cycle_text' | 'cycle_estimate_days'>): { days: number | null; guessed: boolean } {
  if (c.cycle_days && c.cycle_days > 0) return { days: c.cycle_days, guessed: false }
  const t = (c.cycle_text ?? '').trim().toLowerCase()
  if (t && (NO_FIXED_CYCLE.test(t) || /^(one[\s-]?off|just[\s-]?once|doesn.?t want|not wanted)/.test(t))) return { days: null, guessed: false }
  const est = c.cycle_estimate_days
  if (est && est >= 7 && est <= 400) return { days: est, guessed: true }
  return { days: null, guessed: false }
}

// Due's "Next week" (Liam, 11 Oct 2026; 0063): pushed out of Due until a day still to come. Nothing about the customer
// changes; the Diary's Due and Messages leave them alone until then. Per job: an extra job's entry carries its own.
export function pushedNow(c: Pick<Customer, 'due_pushed_until'>, today: string = todayISO()): boolean {
  return typeof c.due_pushed_until === 'string' && c.due_pushed_until > today
}

// Pushed and nothing written down since: what Messages and Needs you leave alone. A day someone has booked since (not the
// app's own suggestion) is real again, push or no push (round 1 review).
export function pushedAndUnbooked(c: Pick<Customer, 'due_pushed_until' | 'next_booked' | 'next_booked_source'>, today: string = todayISO()): boolean {
  return pushedNow(c, today) && !(c.next_booked && c.next_booked_source !== 'suggested' && c.next_booked_source !== 'tentative')
}

// The day a push lasts until: the Monday after this week, on the round's own calendar.
export function nextMondayAfter(today: string): string {
  const d = new Date(`${today}T12:00:00`)
  return addDaysISO(today, 7 - ((d.getDay() + 6) % 7))
}

export function dueDate(c: Customer): string | null {
  const { days } = effectiveCycle(c)
  return c.last_clean && days ? cyclesOn(c.last_clean, days) : null
}

export function dueIsGuessed(c: Customer): boolean {
  return effectiveCycle(c).guessed
}

// Why a customer's record is incomplete — listed rather than just flagged, so
// the fix is visible where the customer is edited. Zero is a known price;
// only null is missing.
export function detailsReasons(c: Pick<Customer, 'reg_price_pence' | 'cycle_days' | 'cycle_text'>): string[] {
  const reasons: string[] = []
  if (c.reg_price_pence === null) reasons.push('no regular price')
  if (!cycleAgreed(c)) reasons.push('no cycle agreed')
  return reasons
}

export function needsDetails(c: Customer): boolean {
  return detailsReasons(c).length > 0
}

export function matchesSearch(c: Customer, query: string): boolean {
  const q = query.trim().toLowerCase()
  // Notes too: what used to be typed in Area ("To call", "1.5x insides") moved there on 30 Sep 2026 (Astra).
  const fields = [c.name, c.address, c.area, c.postcode, c.phone, c.notes].map(v => (v ?? '').toLowerCase())
  return !q || fields.some(v => v.includes(q)) || [c.postcode, c.phone].some(v =>
    (v ?? '').toLowerCase().replace(/\s/g, '').includes(q.replace(/\s/g, '')))
}

export function isContact(cl: Pick<Clean, 'source' | 'confirmed' | 'state'>): boolean {
  return cl.state === 'booked' && cl.source === 'tentative' && !cl.confirmed
}

// A visit whose date passed without anyone marking it done or cancelled is a
// MISSED visit (WEEK-PANEL-NEXT-4 item B): it stays in the diary's review box
// as the record, but the customer must also flow to Due this week and to
// Messages instead of being stuck in the box. Contact reminders (tentative,
// unconfirmed) are not visits anyone expected, so they are not missed — the
// same exclusion isContact makes everywhere else.
export function isMissed(cl: Pick<Clean, 'state' | 'source' | 'confirmed' | 'date'>, today: string): boolean {
  return cl.state === 'booked' && !isContact(cl) && cl.date < today
}

// What the diary's "Past bookings to review" box is actually for: a missed
// visit the app cannot decide about on its own. A day that passed without
// being marked done and without being cancelled could be three things — it
// was cleaned and nobody recorded it, the customer called it off or nobody
// got in, or nobody replied and it never happened — and only an owner knows
// which. Once they have said (reviewed_on, migration 0016) the visit leaves
// the box for good; the customer carries on in Due this week, which is where
// the new day gets chosen, so nothing is lost by the box going quiet.
export function needsReview(cl: Pick<Clean, 'state' | 'source' | 'confirmed' | 'date' | 'reviewed_on'>, today: string): boolean {
  return isMissed(cl, today) && !cl.reviewed_on
}

// The customer half of the missed-visit test. The server can NEVER hand back
// a past next_booked — every query behind that field is `date >= today`
// (functions/api/customers/index.ts) — so the missed visit reaches the
// customer row as the overdue_bookings count instead, whose WHERE clause is
// exactly isMissed (booked, past, not an unconfirmed tentative), plus the
// missed_visit_id / _version / _date of the oldest one for the move. Optional
// fields: a cached payload from before they existed simply reads as no missed
// visit, which is what that screen showed anyway.
export function missedVisit(c: Pick<Customer, 'overdue_bookings'>): boolean {
  return !!c.overdue_bookings
}

// The missed visit has already been dealt with: a real visit is in the diary
// AHEAD of that customer, so the past one is a record rather than a job. An
// agreed future date settles it outright; otherwise the earliest future
// booking counts only when a person put it there — the app's own suggestion
// is a date nobody has acted on, and a tentative row is a contact prompt, not
// a visit. Needed because only two paths MOVE the missed row (placing from
// Due this week, sending from Messages); booking someone back in from the
// booking panel creates a visit BESIDE it, and the old one then sat in "Past
// bookings to review" and in Due this week with an agreed visit five days out
// (Liam: Valerie, booked 1 Sep, agreed again for 25 Sep, 2026-09-18).
// Deliberately conservative when only a LATER future visit is the human one:
// next_booked_source describes the earliest, which is all the customer row
// carries, so they stay listed — showing someone twice beats dropping someone
// nobody has placed.
export function rebooked(c: Pick<Customer, 'next_confirmed' | 'next_booked' | 'next_booked_source'>): boolean {
  if (c.next_confirmed) return true
  if (!c.next_booked) return false
  return c.next_booked_source !== 'suggested' && c.next_booked_source !== 'tentative'
}

export function isSuggestion(cl: Pick<Clean, 'source' | 'confirmed' | 'state'>): boolean {
  return cl.state === 'booked' && cl.source === 'suggested' && !cl.confirmed
}

// A visit that has a WEEK but no day yet. Two ways in, one state:
//
// - it came off the spreadsheet, which recorded weeks rather than days
//   (created_by 'importer', and no human has since moved it, which would have
//   stamped source 'manual');
// - a human put the customer in a week on purpose and will choose the day
//   later (source 'week') — Janet asked to skip a visit and wanted trying
//   again the week of the 28th, with nothing pinned to a day (Liam,
//   2026-09-18).
//
// These rows live in the week panel, NOT on a day card: the card would be
// pretending to hold work nobody has scheduled, and the day's money, workload
// and map all read from that same list. Dropping one on a day moves it and
// the server stamps it 'manual', which is what takes it out of the panel.
// Agreement ends it too — a confirmed visit has a day by definition.
export function isUnplaced(
  cl: Pick<Clean, 'state' | 'confirmed' | 'source' | 'date' | 'created_by'>,
  from: string,
  to: string,
): boolean {
  if (cl.state !== 'booked' || cl.confirmed) return false
  if (cl.date < from || cl.date > to) return false
  if (isContact(cl) || isSuggestion(cl)) return false
  if (cl.source === 'week') return true
  return cl.source !== 'manual' && cl.created_by === 'importer'
}

// Which of the two it is, for the wording on the row: the spreadsheet's own
// week, or a week somebody chose.
export function fromTheSheet(cl: Pick<Clean, 'source' | 'created_by'>): boolean {
  return cl.source !== 'week' && cl.created_by === 'importer'
}

export function workload(rows: Clean[]): string {
  const visits = rows.filter(c => c.state !== 'cancelled' && !isContact(c))
  const known = visits.reduce((n, c) => n + (c.duration_minutes ?? 0), 0)
  const missing = visits.filter(c => !c.duration_minutes).length
  return `${visits.length} job${visits.length === 1 ? '' : 's'} · ${Math.floor(known / 60)}h ${known % 60}m known${missing ? ` · ${missing} duration${missing === 1 ? '' : 's'} missing` : ''}`
}

export function overlapNames(rows: Clean[], date: string, time: string, duration: number | null, worker: string, exclude?: number): string[] {
  const minutes = (s: string | null): number | null => {
    if (!s || !/^([01]\d|2[0-3]):[0-5]\d$/.test(s)) return null
    return Number(s.slice(0, 2)) * 60 + Number(s.slice(3))
  }
  const start = minutes(time)
  if (start === null || !duration || !worker.trim()) return []
  return rows.filter(c => {
    const other = minutes(c.time_slot)
    return c.id !== exclude && c.date === date && c.state !== 'cancelled' && !isContact(c) &&
      c.worker?.trim().toLowerCase() === worker.trim().toLowerCase() && other !== null && !!c.duration_minutes &&
      start < other + c.duration_minutes && other < start + duration
  }).map(c => c.customer_name ?? 'Another job')
}

export function nextPlanningDate(c: Customer): string {
  const due = c.next_booked || dueDate(c)
  return due && due >= todayISO() ? due : todayISO()
}

// ---- Where the customer stands, and what a visit still needs (Diary + Messages, Liam approved 8 Oct 2026) ----

// "Where they stand" (Liam, 8 Oct 2026): one box with one choice instead of separate Round lists and Needs a
// visit forms. Read in the same order as the server (functions/lib/customer-context.ts standingOf). Kept here,
// with no screen code around it, so the Diary, Needs you and Messages all read the one rule.
type StandingFields = Pick<Customer, 'status' | 'customer_list' | 'messages_shelf' | 'message_reply' | 'follow_up_on'>
export type Standing = 'round' | 'visit' | 'later' | 'paused' | 'recanvass' | 'taken_off' | 'lapsed'
export const recanvass = (c: Pick<Customer, 'customer_list'>): boolean => c.customer_list === 'recanvass'
export const takenOff = (c: Pick<Customer, 'customer_list' | 'status' | 'message_reply'>): boolean => c.customer_list === 'taken_off' || !c.customer_list && c.status === 'paused' && c.message_reply === 'stopped'
export const shelved = (c: Pick<Customer, 'messages_shelf'>): boolean => c.messages_shelf === 'visit'
export function standingOf(c: StandingFields, today = todayISO()): Standing {
  if (c.customer_list === 'recanvass' || c.customer_list === 'taken_off') return c.customer_list
  if (takenOff(c)) return 'taken_off'
  if (shelved(c)) return 'visit'
  if (c.status === 'paused') return 'paused'
  if (c.status === 'lapsed') return 'lapsed'
  if (c.follow_up_on && c.follow_up_on > today) return 'later'
  return 'round'
}

// What an upcoming visit still needs from the owner. ONE answer for the Diary's date strip, the visit's own words,
// Needs you ("Visits to confirm" / "Visits to check") and Messages, so a person counted in one is counted in all:
//   'day'   - the day is not agreed yet;
//   'time'  - the day is agreed and a time is set, but that exact time is not agreed (time_agreed_for holds
//             "<date>|<time>", rebook-message.ts timeIsAgreed; written out here because the small tests load this
//             file on its own, and scripts/visit-words.test.mjs fails if the two ever disagree);
//   'check' - the customer said something that makes asking the wrong next step: Needs a visit, paused, stopped,
//             Taken off, Recanvass, lapsed, or "ask again" on a later date than this visit. Deciding about the
//             visit comes first, whatever was agreed;
//   null    - nothing: agreed (with no set time, or the time agreed too), done, cancelled, already past (Plan work's
//             Review owns those), Roundly's own suggestion or a contact reminder (neither is a visit anyone booked).
export type ConfirmNeed = 'day' | 'time' | 'check' | null
export type NeedVisit = Pick<Clean, 'date' | 'confirmed' | 'source'> & { state?: Clean['state']; time_slot?: string | null; time_agreed_for?: string | null }
const TO_CHECK: ReadonlySet<Standing> = new Set<Standing>(['visit', 'paused', 'recanvass', 'taken_off', 'lapsed'])
export function confirmNeed(v: NeedVisit, c: Partial<StandingFields> | null, today = todayISO()): ConfirmNeed {
  const visit = { ...v, state: v.state ?? 'booked' }
  if (visit.state !== 'booked' || visit.date < today || isContact(visit) || isSuggestion(visit)) return null
  if (c) {
    const standing = standingOf(c as StandingFields, today)
    if (TO_CHECK.has(standing) || standing === 'later' && !!c.follow_up_on && visit.date < c.follow_up_on) return 'check'
  }
  if (!visit.confirmed) return 'day'
  // With no set times (Your business), a visit has no time to agree: visibleTime is null.
  const time = visibleTime(visit)
  return time && visit.time_agreed_for !== `${visit.date}|${time}` ? 'time' : null
}

type PencilDayVisit = NeedVisit & Partial<Pick<Clean, 'created_by' | 'customer_status' | 'customer_list' | 'customer_messages_shelf' | 'customer_message_reply' | 'customer_follow_up_on'>>
// Pencilled always means this real visit's DAY is not agreed. Today is included; past work is review's.
// Joined Diary fields supply standing when there is no separate customer record.
export function isPencilledVisit(v: PencilDayVisit, c?: Partial<StandingFields> | null, today = todayISO()): boolean {
  const visit = { ...v, state: v.state ?? 'booked', created_by: v.created_by ?? null }
  const customer = c === undefined ? { status: v.customer_status as Customer['status'], customer_list: v.customer_list,
    messages_shelf: v.customer_messages_shelf, message_reply: v.customer_message_reply, follow_up_on: v.customer_follow_up_on } : c
  return !isUnplaced(visit, v.date, v.date) && confirmNeed(visit, customer, today) === 'day'
}

// A customer's booked visits from today on, every job (GET /api/customers upcoming_visits, oldest first). Null when
// the payload predates the field: the answer is unknown then, not "nothing to confirm".
// moved_from / moved_on: an agreed visit moved to another day and not agreed again (functions/lib/cleans.ts movedFromSql).
export interface UpcomingVisit { id: number; date: string; job_id: number | null; confirmed: 0 | 1; source: Clean['source']; time_slot: string | null; time_agreed_for: string | null; moved_from?: string | null; moved_on?: string | null; moved_told?: 0 | 1 | null; dated_on?: string | null }
export function upcomingVisits(c: Pick<Customer, 'upcoming_visits'>): UpcomingVisit[] | null {
  const raw = c.upcoming_visits
  if (raw === undefined || raw === null) return raw === null ? [] : null
  if (Array.isArray(raw)) return raw
  try { const list = JSON.parse(raw) as unknown; return Array.isArray(list) ? list as UpcomingVisit[] : null } catch { return null }
}

// confirmNeed over every upcoming visit of one customer: how many need the day, the time, a check, and the
// earliest of each. Needs you counts a customer in "Visits to confirm" when day + time > 0 and in "Visits to
// check" when check > 0 (never both for one visit). Null when the payload cannot say (see upcomingVisits).
export interface CustomerNeed { day: number; time: number; check: number; firstConfirm: string | null; firstCheck: string | null }
export function customerConfirmNeed(c: Pick<Customer, 'upcoming_visits'> & Partial<StandingFields>, today = todayISO()): CustomerNeed | null {
  const visits = upcomingVisits(c)
  if (!visits) return null
  const need: CustomerNeed = { day: 0, time: 0, check: 0, firstConfirm: null, firstCheck: null }
  for (const v of visits) {
    const n = confirmNeed({ ...v, state: 'booked' }, c, today)
    if (!n) continue
    need[n]++
    if (n === 'check') { if (!need.firstCheck || v.date < need.firstCheck) need.firstCheck = v.date }
    else if (!need.firstConfirm || v.date < need.firstConfirm) need.firstConfirm = v.date
  }
  return need
}
